import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtDate, STATUS_META } from "@/lib/helpers";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, ClipboardCheck, PenLine, GraduationCap } from "lucide-react";
import { useNavigate } from "react-router-dom";

const CATS = ["Todos", "DNI", "Reconocimiento Médico", "Curso PRL", "EPI", "ITV"];

export default function Alerts() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("Todos");
  const [status, setStatus] = useState("todos");

  useEffect(() => {
    api.get("/alerts").then((r) => setData(r.data)).catch(() => setData({ alerts: [] }));
  }, []);

  if (!data) return <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>;

  const filtered = data.alerts.filter(
    (a) => (cat === "Todos" || a.category === cat) && (status === "todos" || a.status === status)
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Avisos y Caducidades</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Elementos caducados o que vencen en los próximos 30 días.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {CATS.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`filter-cat-${c}`}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              cat === c ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
            }`}>
            {c}
          </button>
        ))}
        <div className="w-px bg-border mx-1" />
        {[["todos", "Todos"], ["caducado", "Caducados"], ["proximo", "Próximos"]].map(([v, l]) => (
          <button key={v} onClick={() => setStatus(v)} data-testid={`filter-status-${v}`}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              status === v ? "bg-accent text-white border-accent" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
            }`}>
            {l}
          </button>
        ))}
      </div>

      {data.ending_trainings?.length > 0 && (
        <Card className="overflow-hidden border-sky-200" data-testid="ending-trainings-card">
          <div className="flex items-center gap-2 p-4 bg-sky-50 border-b border-sky-200">
            <GraduationCap className="h-5 w-5 text-sky-600" />
            <h2 className="font-display font-bold text-sky-900">Cursos de formación que finalizan esta semana</h2>
            <Badge className="ml-auto bg-sky-100 text-sky-800 border border-sky-200">{data.counts.fin_formacion}</Badge>
          </div>
          <div className="divide-y">
            {data.ending_trainings.map((s) => (
              <button key={s.id} onClick={() => navigate("/alumnos")} className="w-full text-left flex items-center justify-between gap-4 p-4 hover:bg-slate-50" data-testid={`ending-training-${s.id}`}>
                <div className="min-w-0"><p className="text-sm font-medium">{s.name}</p><p className="text-xs text-muted-foreground">{s.center} · {s.hours} h · {s.phone || "sin teléfono"}</p></div>
                <div className="text-right shrink-0 text-xs">
                  <p className="font-mono">{fmtDate(s.end_date)}</p>
                  <p className={s.days <= 2 ? "text-red-700" : "text-sky-700"}>{s.days < 0 ? `terminó hace ${-s.days} días` : s.days === 0 ? "termina hoy" : `termina en ${s.days} días`}</p>
                </div>
              </button>
            ))}
          </div>
        </Card>
      )}

      {data.pending_signatures?.length > 0 && (
        <Card className="overflow-hidden border-amber-200" data-testid="pending-signatures-card">
          <div className="flex items-center gap-2 p-4 bg-amber-50 border-b border-amber-200">
            <PenLine className="h-5 w-5 text-amber-600" />
            <h2 className="font-display font-bold text-amber-900">EPIs entregados sin firma del trabajador</h2>
            <Badge className="ml-auto bg-amber-100 text-amber-800 border border-amber-200">{data.counts.sin_firma}</Badge>
          </div>
          <div className="divide-y">
            {data.pending_signatures.map((p) => (
              <button key={p.employee_id} onClick={() => navigate("/empleados")} className="w-full text-left flex items-center justify-between gap-4 p-4 hover:bg-slate-50"
                data-testid={`pending-signature-${p.employee_id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium">{p.employee_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{p.items.join(", ")}</p>
                </div>
                <div className="text-right shrink-0 text-xs text-muted-foreground">
                  {p.count} EPI(s) · desde {fmtDate(p.oldest_delivery)}
                  {p.days > 0 && <span className="block text-amber-700">{p.days} días sin firmar</span>}
                </div>
              </button>
            ))}
          </div>
        </Card>
      )}

      <Card className="overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground text-sm flex flex-col items-center gap-2">
            <ClipboardCheck className="h-10 w-10 text-emerald-500" />
            No hay avisos para este filtro.
          </div>
        ) : (
          <div className="divide-y" data-testid="alerts-table">
            {filtered.map((a, i) => {
              const meta = STATUS_META[a.status];
              return (
                <div key={i} className="flex items-center justify-between gap-4 p-4 hover:bg-slate-50">
                  <div className="flex items-center gap-3 min-w-0">
                    <AlertTriangle className={`h-5 w-5 shrink-0 ${a.status === "caducado" ? "text-red-500" : "text-amber-500"}`} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{a.employee_name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        <span className="font-medium">{a.category}</span> · {a.item}
                      </p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-mono">{fmtDate(a.expiry)}</p>
                    <Badge className={meta.cls}>
                      {a.status === "caducado" ? `Hace ${Math.abs(a.days)} días` : `En ${a.days} días`}
                    </Badge>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
